"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PartnerDealLinkSchema = exports.PARTNER_LINK_STATUSES = void 0;
exports.linkUsable = linkUsable;
exports.displayStatus = displayStatus;
exports.buildLinkCode = buildLinkCode;
exports.linkShareUrl = linkShareUrl;
const zod_1 = require("zod");
/**
 * PARTNER DEAL LINKS — one partner, many deals, each with its own limited code.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS REPLACES
 * ---------------------------------------------------------------------------
 *
 * A partner had ONE `assignedDealId`. That cannot express "market this
 * launch offer to 10 vape shops until October, and this ISO starter to
 * everyone, indefinitely" — which is the actual business.
 *
 * Each link is a partner + deal pair with its OWN code, cap and expiry, so a
 * limited offer can be handed to specific people, tracked, and expired,
 * without touching the deal itself (the deal's own `maxUses` is a global cap
 * across every partner, which is a different thing and stays as it is).
 *
 * ---------------------------------------------------------------------------
 * THE CAP COUNTS PAID CONVERSIONS
 * ---------------------------------------------------------------------------
 *
 * `usedCount` increments when someone PAYS, never when they sign up. A cap that
 * burned on signup would be spent by tire-kickers and abandoned carts: ten
 * offers could be consumed while nobody ever paid, and the partner would have
 * nothing to show for a campaign that cost them their whole allocation.
 *
 * The trade-off is accepted deliberately: more signups than remaining seats can
 * be in flight at once, so a link can slightly oversell. `remainingUses` is
 * therefore a FORECAST, not a reservation, and `linkUsable` is checked again at
 * conversion — never only at signup.
 */
exports.PARTNER_LINK_STATUSES = ['active', 'paused', 'expired', 'exhausted'];
exports.PartnerDealLinkSchema = zod_1.z.object({
    linkId: zod_1.z.string().min(1),
    partnerId: zod_1.z.string().min(1),
    dealId: zod_1.z.string().min(1),
    /** The shareable code. UNIQUE across the platform — this is what a customer types. */
    code: zod_1.z.string().min(1),
    /** Operator's own label: "Vape launch — Midwest", shown in listings. */
    label: zod_1.z.string().optional(),
    /**
     * Paid conversions this link may produce. `null` = unlimited.
     * Never 0 for "unlimited" — 0 legitimately means "no seats left".
     */
    maxUses: zod_1.z.number().int().min(0).nullable().optional(),
    /** Paid conversions so far. SERVER-OWNED; only the conversion path increments it. */
    usedCount: zod_1.z.number().int().min(0).default(0),
    /** Signups that used the code, paid or not. Reporting only — never gates. */
    signupCount: zod_1.z.number().int().min(0).default(0),
    /** Epoch ms after which the code stops working. Absent = never expires. */
    expiresAt: zod_1.z.number().optional(),
    /**
     * Operator status. `expired`/`exhausted` are DERIVED for display by
     * `linkUsable` and must not be trusted as stored values — a row can sit at
     * 'active' long after its date passed because nothing sweeps it.
     */
    status: zod_1.z.enum(exports.PARTNER_LINK_STATUSES).default('active'),
    /** The commission plan this link's sales pay under. Absent = normal cascade. */
    commissionPlanId: zod_1.z.string().optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
    createdBy: zod_1.z.string().optional(),
});
/**
 * Can this link still be used?
 *
 * Evaluated from the DATE and the COUNT, not from the stored `status`: nothing
 * sweeps rows, so a link whose expiry passed last week is still stored as
 * 'active'. Trusting the stored value would keep an expired offer live — the
 * same "time, not just status" rule the partner balance follows, for the same
 * reason.
 *
 * `paused` is the one status that IS authoritative, because it is an explicit
 * operator decision rather than something derivable.
 *
 * MUST be checked again at conversion, not only at signup: the cap counts paid
 * conversions, so seats can be taken between the two.
 */
function linkUsable(link, nowMs) {
    if (!link)
        return { usable: false, reason: 'expired', remainingUses: 0 };
    const unlimited = link.maxUses === null || link.maxUses === undefined;
    const used = Math.max(0, Math.trunc(link.usedCount || 0));
    const remainingUses = unlimited ? null : Math.max(0, link.maxUses - used);
    if (link.status === 'paused')
        return { usable: false, reason: 'paused', remainingUses };
    if (typeof link.expiresAt === 'number' && link.expiresAt <= nowMs) {
        return { usable: false, reason: 'expired', remainingUses };
    }
    if (!unlimited && remainingUses !== null && remainingUses <= 0) {
        return { usable: false, reason: 'exhausted', remainingUses: 0 };
    }
    return { usable: true, reason: 'ok', remainingUses };
}
/** What an operator sees in a list. Derived, never stored. */
function displayStatus(link, nowMs) {
    const u = linkUsable(link, nowMs);
    if (u.usable)
        return 'active';
    return u.reason === 'ok' ? 'active' : u.reason;
}
/**
 * A short, unambiguous code for a new link.
 *
 * Excludes characters people mistype when reading a code aloud or off a
 * screen — 0/O, 1/I/L — because these get dictated over the phone. The caller
 * supplies randomness so this stays pure and testable, and MUST check the
 * result is unused before storing it.
 */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
function buildLinkCode(dealCode, partnerCode, randomFn) {
    const suffix = Array.from({ length: 4 }, () => CODE_ALPHABET[Math.floor(randomFn() * CODE_ALPHABET.length) % CODE_ALPHABET.length]).join('');
    const dealPart = (dealCode || 'DEAL').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    const partnerPart = (partnerCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    return [dealPart, partnerPart, suffix].filter(Boolean).join('-');
}
/**
 * The URL a partner shares. Built in ONE place so the portal, the admin and any
 * email can never drift into producing different links for the same code.
 */
function linkShareUrl(origin, code) {
    const base = (origin || '').replace(/\/+$/, '');
    return `${base}/signup?code=${encodeURIComponent(code)}`;
}
//# sourceMappingURL=partnerDealLink.js.map